import { Close, Expand } from "@mui/icons-material"
import Refresh from "@mui/icons-material/Refresh"
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, DataGridProps } from "@mui/x-data-grid"
import { TFunction } from "i18next"
import moment from "moment"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { JsonView } from "react-json-view-lite"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useGetThingsQuery } from "../features/things/thingsApi"
import SaLayout from "./SaLayout"

const defaultColWidth = 230

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaSeedTrackersPage: FC = () => {
  const { t } = useTranslation()

  const q = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetSeedTrackers,
    },
  })

  const [previewData, setPreviewData] = useState<Record<string, any> | null>(
    null,
  )

  const columns = useCallback(
    (t: TFunction): DataGridProps["columns"] => [
      {
        field: "preview",
        headerName: "",
        minWidth: 40,
        renderCell: (params) => {
          return (
            <IconButton onClick={() => setPreviewData(params.row)}>
              <Expand />
            </IconButton>
          )
        },
      },
      {
        field: "createdAt",
        headerName: `📅 ${t("createdAt")}`,
        minWidth: defaultColWidth,
        // render cell with moment fromNow
        renderCell: (params) => {
          return (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" noWrap>
                {params.value
                  ? moment(params.value).format("YYYY MM-DD HH:mm:ss")
                  : ""}
              </Typography>
              <Typography variant="caption" noWrap>
                {params.value ? moment(params.value).fromNow() : ""}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: "updatedAt",
        headerName: `📅 ${t("updatedAt")}`,
        minWidth: defaultColWidth,
        renderCell: (params) => {
          return (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" noWrap>
                {params.value
                  ? moment(params.value).format("YYYY MM-DD HH:mm:ss")
                  : ""}
              </Typography>
              <Typography variant="caption" noWrap>
                {params.value ? moment(params.value).fromNow() : ""}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: "status",
        headerName: "status",
        minWidth: defaultColWidth,
        // render cell with status : green for success, red for failed, orange for running
        renderCell: (params) => {
          return (
            <Stack direction="column" spacing={1}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  color:
                    params.value === "SUCCESS"
                      ? "green"
                      : params.value === "FAILED"
                      ? "red"
                      : "orange",
                }}
              >
                {params.value}
              </Typography>
              {params.row.reason && (
                <Typography variant="caption" noWrap>
                  {params.row.reason}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: "id",
        headerName: "id",
        minWidth: defaultColWidth,
      },
      {
        field: "seederVersion",
        headerName: "seederVersion",
        minWidth: defaultColWidth,
      },
      {
        field: "lastAppliedIdx",
        headerName: "lastAppliedIdx",
        minWidth: defaultColWidth,
      },
      {
        field: "lastAppliedDevIdx",
        headerName: "lastAppliedDevIdx",
        minWidth: defaultColWidth,
      },
      {
        field: "appliedIdxs",
        headerName: "appliedIdxs",
        minWidth: defaultColWidth,
      },
      {
        field: "appliedDevIdxs",
        headerName: "appliedDevIdxs",
        minWidth: defaultColWidth,
      },
    ],
    [],
  )

  return (
    <SaLayout selectedMenuItemLabelKey="seedTrackers">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {t("seedTrackers")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {q.isLoading || q.isFetching ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={q.refetch}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Data grid card */}
        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <DataGrid
            columns={columns(t)}
            rows={q.data?.things || []}
            autoHeight
            rowSelection={false}
            sx={{
              // width: "100%",
              ".MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
              },
            }}
          />
        </Box>
      </Box>
      {previewData && (
        <Dialog data-ui="UI-SL2W-221"
          open={true}
          onClose={() => setPreviewData(null)}
          fullWidth
          maxWidth="md"
        >
          <Stack direction="column" spacing={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">{t("preview")}</Typography>
              <IconButton onClick={() => setPreviewData(null)}>
                <Close />
              </IconButton>
            </Stack>
            <JsonView data={previewData} />
          </Stack>
        </Dialog>
      )}
    </SaLayout>
  )
}

export default SaSeedTrackersPage
